import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Plus, Loader2, Trash2, Settings, Zap, Building2, Shield, Users
} from 'lucide-react';
import api from '../../lib/api';

const categoryIcons = {
  entity: Building2,
  trust: Shield,
  governance: Users
};

export default function RuleBuilderPage() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    category: 'entity',
    conditionField: 'industry',
    conditionValue: '',
    actionType: 'addEntity',
    actionValue: '',
    priority: 1
  });

  useEffect(() => {
    loadRules();
  }, []);

  const loadRules = async () => {
    try {
      const res = await api.get('/rules');
      setRules(res.data);
    } catch (err) {
      console.error('Failed to load rules:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRule = async () => {
    setSaving(true);
    try {
      const payload = {
        category: formData.category,
        condition: { [formData.conditionField]: formData.conditionValue },
        action: { [formData.actionType]: formData.actionValue },
        priority: formData.priority
      };
      
      await api.post('/rules', payload);
      await loadRules();
      setFormData({
        ...formData,
        conditionValue: '',
        actionValue: ''
      });
    } catch (err) {
      console.error('Failed to create rule:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    try {
      await api.delete(`/rules/${ruleId}`);
      setRules(rules.filter(r => r.id !== ruleId));
    } catch (err) {
      console.error('Failed to delete rule:', err);
    }
  };

  const categories = [
    { value: 'entity', label: 'Entity' },
    { value: 'trust', label: 'Trust' },
    { value: 'governance', label: 'Governance' }
  ];

  const conditionFields = [
    { value: 'industry', label: 'Industry' },
    { value: 'goal', label: 'Goal' },
    { value: 'revenue', label: 'Annual Revenue' },
    { value: 'jurisdiction', label: 'Jurisdiction' }
  ];

  const actionTypes = [
    { value: 'addEntity', label: 'Add Entity' },
    { value: 'setHolding', label: 'Set Holding Company' },
    { value: 'addTrust', label: 'Add Trust' },
    { value: 'setJurisdiction', label: 'Set Jurisdiction' },
    { value: 'requireAudit', label: 'Require Audit' }
  ];

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="rule-builder-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold font-['Outfit'] flex items-center gap-3">
            <Settings className="w-8 h-8 text-blue-400" />
            Rule Builder
          </h1>
          <p className="text-slate-400 mt-1">Create custom rules for automatic structure generation</p>
        </div>

        {/* Rule Creator */}
        <div className="stat-card" data-testid="rule-creator">
          <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-400" />
            Create New Rule
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {/* Category */}
            <div className="space-y-2">
              <Label className="text-slate-300">Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger className="bg-slate-900 border-slate-800" data-testid="category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  {categories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Condition Field */}
            <div className="space-y-2">
              <Label className="text-slate-300">When</Label>
              <Select
                value={formData.conditionField}
                onValueChange={(value) => setFormData({ ...formData, conditionField: value })}
              >
                <SelectTrigger className="bg-slate-900 border-slate-800" data-testid="condition-field-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  {conditionFields.map(field => (
                    <SelectItem key={field.value} value={field.value}>{field.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Condition Value */}
            <div className="space-y-2">
              <Label className="text-slate-300">Equals</Label>
              <Input
                value={formData.conditionValue}
                onChange={(e) => setFormData({ ...formData, conditionValue: e.target.value })}
                placeholder="e.g., real_estate, technology"
                className="bg-slate-900 border-slate-800"
                data-testid="condition-value-input"
              />
            </div>

            {/* Action Type */}
            <div className="space-y-2">
              <Label className="text-slate-300">Then</Label>
              <Select
                value={formData.actionType}
                onValueChange={(value) => setFormData({ ...formData, actionType: value })}
              >
                <SelectTrigger className="bg-slate-900 border-slate-800" data-testid="action-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  {actionTypes.map(action => (
                    <SelectItem key={action.value} value={action.value}>{action.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Action Value */}
            <div className="space-y-2">
              <Label className="text-slate-300">Value</Label>
              <Input
                value={formData.actionValue}
                onChange={(e) => setFormData({ ...formData, actionValue: e.target.value })}
                placeholder="e.g., Delaware LLC, Wyoming Trust"
                className="bg-slate-900 border-slate-800"
                data-testid="action-value-input"
              />
            </div>

            {/* Priority */}
            <div className="space-y-2">
              <Label className="text-slate-300">Priority</Label>
              <Input
                type="number"
                min="1"
                max="100"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 1 })}
                className="bg-slate-900 border-slate-800"
                data-testid="priority-input"
              />
            </div>
          </div>

          <Button
            onClick={handleCreateRule}
            disabled={saving || !formData.conditionValue || !formData.actionValue}
            className="gradient-accent hover:opacity-90"
            data-testid="create-rule-btn"
          >
            {saving ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Plus className="w-5 h-5 mr-2" />
                Create Rule
              </>
            )}
          </Button>
        </div>

        {/* Active Rules */}
        <div>
          <h2 className="text-xl font-semibold mb-4">Active Rules</h2>
          
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : rules.length === 0 ? (
            <div className="stat-card text-center py-12" data-testid="no-rules">
              <Settings className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No rules created yet</h3>
              <p className="text-slate-400">Create your first rule to automate structure generation</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {rules.map((rule) => {
                const Icon = categoryIcons[rule.category] || Settings;
                return (
                  <div key={rule.id} className="stat-card group" data-testid={`rule-${rule.id}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                          <p className="font-medium capitalize">{rule.category} Rule</p>
                          <p className="text-xs text-slate-500">Priority: {rule.priority}</p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteRule(rule.id)}
                        className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        data-testid={`delete-rule-${rule.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-slate-500">When:</span>
                        <code className="bg-slate-800 px-2 py-1 rounded text-blue-400">
                          {Object.entries(rule.condition).map(([k, v]) => `${k} = ${v}`).join(', ')}
                        </code>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-500">Then:</span>
                        <code className="bg-slate-800 px-2 py-1 rounded text-emerald-400">
                          {Object.entries(rule.action).map(([k, v]) => `${k}: ${v}`).join(', ')}
                        </code>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
